import React from "react";
import type { IconType } from "react-icons";
import styled from "styled-components";
import {
  SiTypescript,
  SiJavascript,
  SiSwift,
  SiPython,
  SiHtml5,
  SiCss,
  SiOpenjdk,
  SiC,
  SiReact,
  SiReactquery,
  SiVite,
  SiStorybook,
  SiSass,
  SiGraphql,
  SiApachekafka,
  SiRedis,
  SiPostgresql,
  SiTypeorm,
  SiZod,
  SiJest,
  SiTestinglibrary,
  SiPosthog,
  SiGrafana,
  SiNewrelic,
  SiGit,
  SiNodedotjs,
  SiExpress,
  SiDocker,
  SiGithubactions,
  SiPostman,
  SiArgo,
  SiKubernetes,
  SiReactivex,
  SiXcode,
  SiCocoapods,
} from "react-icons/si";
import {
  FaDatabase,
  FaPlug,
  FaServer,
  FaApple,
  FaTable,
  FaCode,
  FaCube,
  FaUniversalAccess,
  FaMobileAlt,
  FaShapes,
  FaChartLine,
  FaClock,
  FaTheaterMasks,
  FaAws,
  FaPaintBrush,
} from "react-icons/fa";
import { colors, radius } from "../theme";
import AppIcon from "../AppIcon";
import { AppScroll, AppEyebrow, AppTitle, SectionLabel } from "./shared";

interface Tech {
  name: string;
  icon: IconType;
  color: string;
}

const INK = colors.ink;

const groups: { label: string; accent: string; items: Tech[] }[] = [
  {
    label: "Languages",
    accent: colors.orange,
    items: [
      { name: "TypeScript", icon: SiTypescript, color: "#3178C6" },
      { name: "JavaScript", icon: SiJavascript, color: "#E8B400" },
      { name: "Swift", icon: SiSwift, color: "#F05138" },
      { name: "SQL", icon: FaDatabase, color: INK },
      { name: "Python", icon: SiPython, color: "#3776AB" },
      { name: "HTML", icon: SiHtml5, color: "#E34F26" },
      { name: "CSS", icon: SiCss, color: "#663399" },
      { name: "Java", icon: SiOpenjdk, color: "#EA2D2E" },
      { name: "C", icon: SiC, color: "#5E97D0" },
    ],
  },
  {
    label: "Frontend",
    accent: colors.sky,
    items: [
      { name: "React", icon: SiReact, color: "#149ECA" },
      { name: "React Native", icon: SiReact, color: "#61DAFB" },
      { name: "Zustand", icon: FaCube, color: INK },
      { name: "TanStack Query", icon: SiReactquery, color: "#FF4154" },
      { name: "TanStack Table", icon: FaTable, color: "#FF4154" },
      { name: "Vite", icon: SiVite, color: "#646CFF" },
      { name: "Storybook", icon: SiStorybook, color: "#FF4785" },
      { name: "SCSS Modules", icon: SiSass, color: "#CC6699" },
      { name: "Styled Components", icon: FaPaintBrush, color: "#DB7093" },
      { name: "Accessibility", icon: FaUniversalAccess, color: "#2A7DE1" },
      { name: "Responsive UI", icon: FaMobileAlt, color: INK },
      { name: "Design Systems", icon: FaShapes, color: "#863EFB" },
      { name: "Data Visualization", icon: FaChartLine, color: "#149ECA" },
      { name: "UIKit", icon: FaApple, color: INK },
      { name: "RxSwift", icon: SiReactivex, color: "#B7178C" },
    ],
  },
  {
    label: "Data & APIs",
    accent: colors.lilac,
    items: [
      { name: "REST", icon: FaServer, color: INK },
      { name: "GraphQL", icon: SiGraphql, color: "#E10098" },
      { name: "WebSockets", icon: FaPlug, color: INK },
      { name: "Kafka", icon: SiApachekafka, color: INK },
      { name: "SQS", icon: FaAws, color: "#FF9900" },
      { name: "Redis", icon: SiRedis, color: "#DC382D" },
      { name: "Postgres", icon: SiPostgresql, color: "#4169E1" },
      { name: "TypeORM", icon: SiTypeorm, color: "#E83524" },
      { name: "Zod", icon: SiZod, color: "#3E67B1" },
      { name: "Cron Jobs", icon: FaClock, color: INK },
    ],
  },
  {
    label: "Testing & Observability",
    accent: colors.green,
    items: [
      { name: "Playwright", icon: FaTheaterMasks, color: "#2EAD33" },
      { name: "Jest", icon: SiJest, color: "#C21325" },
      { name: "Testing Library", icon: SiTestinglibrary, color: "#E33332" },
      { name: "PostHog", icon: SiPosthog, color: "#F54E00" },
      { name: "Grafana Faro", icon: SiGrafana, color: "#F46800" },
      { name: "New Relic", icon: SiNewrelic, color: "#1CE783" },
    ],
  },
  {
    label: "Tools",
    accent: colors.peach,
    items: [
      { name: "Git", icon: SiGit, color: "#F05032" },
      { name: "Node", icon: SiNodedotjs, color: "#5FA04E" },
      { name: "Express", icon: SiExpress, color: INK },
      { name: "Docker", icon: SiDocker, color: "#2496ED" },
      { name: "GitHub Actions", icon: SiGithubactions, color: "#2088FF" },
      { name: "Postman", icon: SiPostman, color: "#FF6C37" },
      { name: "ArgoCD", icon: SiArgo, color: "#EF7B4D" },
      { name: "Kubernetes", icon: SiKubernetes, color: "#326CE5" },
      { name: "TablePlus", icon: FaTable, color: INK },
      { name: "App Center", icon: FaCode, color: INK },
      { name: "CocoaPods", icon: SiCocoapods, color: "#EE3322" },
      { name: "Xcode", icon: SiXcode, color: "#1575F9" },
    ],
  },
];

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(108px, 1fr));
  gap: 12px;
`;

const Tile = styled.div<{ $color: string }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px 8px;
  border: 2px solid ${colors.ink};
  border-radius: ${radius.md};
  background: #fff;
  box-shadow: 3px 3px 0 rgba(29, 29, 31, 0.1);
  cursor: default;
  transition: transform 0.14s ease, background 0.14s ease, box-shadow 0.14s ease;

  svg {
    color: ${({ $color }) => $color};
    transition: color 0.14s ease;
  }

  span {
    font-size: 13px;
    font-weight: 600;
    color: ${colors.inkSoft};
    transition: color 0.14s ease;
  }

  &:hover {
    transform: translate(-2px, -2px);
    box-shadow: 5px 5px 0 rgba(29, 29, 31, 0.18);
    background: ${({ $color }) => $color};

    svg,
    span {
      color: #fff;
    }
  }
`;

const SkillsApp = () => (
  <AppScroll>
    <AppEyebrow>My toolbox</AppEyebrow>
    <AppTitle>Tech Stack</AppTitle>
    {groups.map((group) => (
      <div key={group.label}>
        <SectionLabel
          style={{
            color: colors.ink,
            borderLeft: `6px solid ${group.accent}`,
            paddingLeft: 8,
          }}
        >
          {group.label}
        </SectionLabel>
        <Grid>
          {group.items.map((tech) => (
            <Tile key={tech.name} $color={tech.color} title={tech.name}>
              <AppIcon icon={tech.icon} size={30} />
              <span>{tech.name}</span>
            </Tile>
          ))}
        </Grid>
      </div>
    ))}
  </AppScroll>
);

export default SkillsApp;
